import { PlusIcon, TrashIcon } from '@phosphor-icons/react';
import { Button } from '@/components/ui-kit/button';
import { Input } from '@/components/ui-kit/input';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui-kit/select';
import {
  FIELD_CATEGORIES,
  type FilterFieldConfig,
  type FilterOperator,
} from '../../config/filterable-fields';
import { useFilterableFields } from '../../hooks/use-filterable-fields';

export type FilterRow = { id: string; fieldKey: string; operator: FilterOperator | ''; value: string };
export type FilterMatchType = 'all' | 'any';

export type FilterRowBuilderProps = {
  rows: FilterRow[];
  onChange: (rows: FilterRow[]) => void;
  matchType: FilterMatchType;
  onMatchTypeChange: (matchType: FilterMatchType) => void;
  /**
   * Overrides the live merged useFilterableFields() registry — used by
   * campaign-wizard surfaces (Custom Segment builder, RecipientsPreview's
   * Advanced Filters) to exclude Contacts-page-only fields like
   * subscriptionStatus. Defaults to the full merged registry when absent.
   */
  registry?: FilterFieldConfig[];
};

const OPERATOR_LABELS: Record<FilterOperator, string> = {
  equals: 'Equals',
  notEquals: 'Not equals',
  contains: 'Contains',
  isEmpty: 'Is empty',
  numberRange: 'Between',
  dateRange: 'Date range',
};

/** A multi-select attribute holds a set, so its equality operators read as set membership. */
const MULTI_SELECT_OPERATOR_LABELS: Partial<Record<FilterOperator, string>> = {
  equals: 'Is any of',
  notEquals: 'Is none of',
};

const operatorLabel = (field: FilterFieldConfig | undefined, operator: FilterOperator): string =>
  (field?.valueType === 'multiSelect' && MULTI_SELECT_OPERATOR_LABELS[operator]) || OPERATOR_LABELS[operator];

const emptyRow = (): FilterRow => ({
  id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  fieldKey: '',
  operator: '',
  value: '',
});

/**
 * Two-part draft shapes for numberRange/dateRange rows. `FilterRow.value`
 * stores these JSON-encoded so `parseRowValue` (filter-query-builder.ts) can
 * parse them back into typed Mongo clause values. numberRange only commits
 * to the final `[min, max]` array shape once both sides are valid numbers —
 * until then it round-trips as `{ min, max }` strings, which
 * `parseRowValue` safely treats as incomplete (skips the row) rather than
 * crashing or producing a wrong filter from partial input.
 */
type NumberRangeDraft = { min: string; max: string };
type DateRangeDraft = { from: string; to: string };

const parseNumberRangeDraft = (value: string): NumberRangeDraft => {
  try {
    const parsed = JSON.parse(value);
    if (Array.isArray(parsed) && parsed.length === 2) {
      return {
        min: parsed[0] === undefined || parsed[0] === null ? '' : String(parsed[0]),
        max: parsed[1] === undefined || parsed[1] === null ? '' : String(parsed[1]),
      };
    }
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      const record = parsed as { min?: unknown; max?: unknown };
      return {
        min: typeof record.min === 'string' ? record.min : '',
        max: typeof record.max === 'string' ? record.max : '',
      };
    }
  } catch {
    // Empty/malformed value — fall through to the blank draft.
  }
  return { min: '', max: '' };
};

const serializeNumberRangeDraft = (draft: NumberRangeDraft): string => {
  const minNum = Number(draft.min);
  const maxNum = Number(draft.max);
  if (draft.min !== '' && draft.max !== '' && !Number.isNaN(minNum) && !Number.isNaN(maxNum)) {
    return JSON.stringify([minNum, maxNum]);
  }
  return JSON.stringify(draft);
};

const parseDateRangeDraft = (value: string): DateRangeDraft => {
  try {
    const parsed = JSON.parse(value);
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      const record = parsed as { from?: unknown; to?: unknown };
      return {
        from: typeof record.from === 'string' ? record.from : '',
        to: typeof record.to === 'string' ? record.to : '',
      };
    }
  } catch {
    // Empty/malformed value — fall through to the blank draft.
  }
  return { from: '', to: '' };
};

export const FilterRowBuilder = ({
  rows,
  onChange,
  matchType,
  onMatchTypeChange,
  registry: registryOverride,
}: FilterRowBuilderProps) => {
  const filterableFields = useFilterableFields();
  const registry = registryOverride ?? filterableFields;

  const updateRow = (id: string, patch: Partial<FilterRow>) => {
    onChange(rows.map((row) => (row.id === id ? { ...row, ...patch } : row)));
  };

  const addRow = () => onChange([...rows, emptyRow()]);

  const removeRow = (id: string) => {
    if (rows.length === 1) return;
    onChange(rows.filter((row) => row.id !== id));
  };

  return (
    <div className="space-y-3">
      <div className="inline-flex rounded-md border border-border p-0.5">
        <button
          type="button"
          onClick={() => onMatchTypeChange('all')}
          className={`rounded px-3 py-1.5 text-sm font-medium transition-colors ${
            matchType === 'all' ? 'bg-[#0161AB] text-white' : 'text-muted-foreground hover:bg-accent/40'
          }`}
        >
          Match ALL
        </button>
        <button
          type="button"
          onClick={() => onMatchTypeChange('any')}
          className={`rounded px-3 py-1.5 text-sm font-medium transition-colors ${
            matchType === 'any' ? 'bg-[#0161AB] text-white' : 'text-muted-foreground hover:bg-accent/40'
          }`}
        >
          Match ANY
        </button>
      </div>
      <div className="space-y-2">
        {rows.map((row) => {
          const field = registry.find((candidate) => candidate.key === row.fieldKey);
          const usesValueSelect = field?.valueType === 'select' || field?.valueType === 'boolean';
          const isEmptyOperator = row.operator === 'isEmpty';
          const isNumberRangeOperator = row.operator === 'numberRange';
          const isDateRangeOperator = row.operator === 'dateRange';

          return (
            <div key={row.id} className="grid grid-cols-12 gap-2 items-center">
              <Select
                value={row.fieldKey}
                onValueChange={(fieldKey) => updateRow(row.id, { fieldKey, operator: '', value: '' })}
              >
                <SelectTrigger className="col-span-12 md:col-span-4 focus:ring-[#0161AB] focus-visible:ring-[#0161AB] focus:border-[#0161AB]">
                  <SelectValue placeholder="Select criteria" />
                </SelectTrigger>
                <SelectContent>
                  {FIELD_CATEGORIES.map((category) => {
                    const fieldsInCategory = registry.filter((option) => option.category === category);
                    if (fieldsInCategory.length === 0) return null;

                    return (
                      <SelectGroup key={category}>
                        <SelectLabel>{category}</SelectLabel>
                        {fieldsInCategory.map((option) => (
                          <SelectItem key={option.key} value={option.key}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    );
                  })}
                </SelectContent>
              </Select>

              <Select
                value={row.operator}
                onValueChange={(operator) =>
                  updateRow(row.id, { operator: operator as FilterOperator, value: '' })
                }
                disabled={!field}
              >
                <SelectTrigger className="col-span-12 md:col-span-3 focus:ring-[#0161AB] focus-visible:ring-[#0161AB] focus:border-[#0161AB]">
                  <SelectValue placeholder="Select condition" />
                </SelectTrigger>
                <SelectContent>
                  {field?.operators.map((operator) => (
                    <SelectItem key={operator} value={operator}>
                      {operatorLabel(field, operator)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {isEmptyOperator ? (
                <div className="col-span-11 md:col-span-4 flex items-center text-sm text-muted-foreground">
                  No value needed
                </div>
              ) : isNumberRangeOperator ? (
                <div className="col-span-11 md:col-span-4 flex items-center gap-2">
                  {(() => {
                    const draft = parseNumberRangeDraft(row.value);
                    return (
                      <>
                        <Input
                          type="number"
                          placeholder="Min"
                          value={draft.min}
                          disabled={!field}
                          aria-label="Minimum value"
                          className="focus-visible:ring-[#0161AB] focus-visible:border-[#0161AB]"
                          onChange={(event) =>
                            updateRow(row.id, {
                              value: serializeNumberRangeDraft({ ...draft, min: event.target.value }),
                            })
                          }
                        />
                        <span className="text-muted-foreground">–</span>
                        <Input
                          type="number"
                          placeholder="Max"
                          value={draft.max}
                          disabled={!field}
                          aria-label="Maximum value"
                          className="focus-visible:ring-[#0161AB] focus-visible:border-[#0161AB]"
                          onChange={(event) =>
                            updateRow(row.id, {
                              value: serializeNumberRangeDraft({ ...draft, max: event.target.value }),
                            })
                          }
                        />
                      </>
                    );
                  })()}
                </div>
              ) : isDateRangeOperator ? (
                <div className="col-span-11 md:col-span-4 flex items-center gap-2">
                  {(() => {
                    const draft = parseDateRangeDraft(row.value);
                    return (
                      <>
                        <Input
                          type="date"
                          value={draft.from}
                          disabled={!field}
                          aria-label="From date"
                          className="focus-visible:ring-[#0161AB] focus-visible:border-[#0161AB]"
                          onChange={(event) =>
                            updateRow(row.id, {
                              value: JSON.stringify({ ...draft, from: event.target.value }),
                            })
                          }
                        />
                        <span className="text-muted-foreground">–</span>
                        <Input
                          type="date"
                          value={draft.to}
                          disabled={!field}
                          aria-label="To date"
                          className="focus-visible:ring-[#0161AB] focus-visible:border-[#0161AB]"
                          onChange={(event) =>
                            updateRow(row.id, {
                              value: JSON.stringify({ ...draft, to: event.target.value }),
                            })
                          }
                        />
                      </>
                    );
                  })()}
                </div>
              ) : usesValueSelect ? (
                <Select
                  value={row.value}
                  onValueChange={(value) => updateRow(row.id, { value })}
                  disabled={!field || !row.operator}
                >
                  <SelectTrigger className="col-span-11 md:col-span-4 focus:ring-[#0161AB] focus-visible:ring-[#0161AB] focus:border-[#0161AB]">
                    <SelectValue placeholder="Select value" />
                  </SelectTrigger>
                  <SelectContent>
                    {(field?.valueType === 'boolean'
                      ? [
                          { value: 'true', label: 'Yes' },
                          { value: 'false', label: 'No' },
                        ]
                      : (field?.options ?? [])
                    ).map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <Input
                  type={field?.valueType === 'number' ? 'number' : 'text'}
                  className="col-span-11 md:col-span-4 focus-visible:ring-[#0161AB] focus-visible:border-[#0161AB]"
                  placeholder={field?.valueType === 'multiSelect' ? 'e.g. sports, music' : 'Enter value'}
                  value={row.value}
                  disabled={!field || !row.operator}
                  onChange={(event) => updateRow(row.id, { value: event.target.value })}
                />
              )}

              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="col-span-1"
                aria-label="Remove filter"
                onClick={() => removeRow(row.id)}
              >
                <TrashIcon className="h-4 w-4 text-muted-foreground" />
              </Button>
            </div>
          );
        })}

        <Button type="button" variant="ghost" className="text-[#0161AB]" onClick={addRow}>
          <PlusIcon className="mr-1 h-4 w-4" />
          Add Filter
        </Button>
      </div>
    </div>
  );
};
